import flatpickr from 'flatpickr';
import { Options as FlatpickrOptions, Plugin } from 'flatpickr/dist/types/options';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { getLastTimeSlotHour, getParameterFromGenericListTimeSlot } from '../select/select';
import {
  breakpoints,
  checkNextDaysAutoComplete,
  getDayKey,
  getFormatFromAEMConfig,
  getPageLocale,
  getTimeZoneEnable,
  langLocaleMap,
  selectFirstAvailableDay,
  store,
  yearDropdownPlugin,
} from '@msc-b2c/shared-utils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { DatePickerValidationEnum } from '@msc-b2c/interfaces/enums';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

export class DatePicker {
  private readonly container: HTMLElement;
  private readonly input: HTMLInputElement;
  private instance!: FlatpickrInstance;
  private readonly dateFormat: string;

  constructor(node: HTMLElement) {
    this.container = node;
    this.input = this.container?.querySelector('input.cmp-form-text__text') as HTMLInputElement;
    this.dateFormat = this.input?.getAttribute('data-date-format') ?? 'DD/MM/YYYY';
  }

  async init(): Promise<void> {
    const today = new Date();
    const showTodayAsClickable: boolean =
      this.input.getAttribute('data-today-clickable') === 'true';
    const isTimeZoneEnabled = getTimeZoneEnable();
    const callbackWeekdaysOnly = isTimeZoneEnabled && this.input.name === 'callback.selectedDay';
    // last available slot start hour from AEM time-slot list (fallback 17)
    const lastSlot = showTodayAsClickable ? await getLastTimeSlotHour() : 17;
    const isTodayTheFirst = () => {
      return showTodayAsClickable && new Date().getHours() < lastSlot; // last slot available;
    };
    let startDay = isTimeZoneEnabled ? createDateOnly(new Date()) : new Date();

    let minDate: Date = new Date();
    let maxDate: Date = new Date();
    const showNextPreviousDate: string | null = this.input.getAttribute('data-date-values');
    const defaultDateValue: string | null = this.input.getAttribute('data-date-default');
    const yearToRemoveDateMax: string | null = this.input.getAttribute('data-max-years-values');
    const yearToRemoveDateMin: string | null = this.input.getAttribute('data-min-years-values');

    const placeHolder = this.input.getAttribute('placeholder');

    const dateDisabled: string = this.input?.getAttribute('data-customdatedisable') ?? '';
    const effectiveDateDisabled = appendDisabledDays(dateDisabled, callbackWeekdaysOnly);
    const { disabledDays, disabledDates } = processExcludedDates(effectiveDateDisabled);
    let dayOffset = Number(this.input?.getAttribute('data-date-days'));

    startDay = getStartDay(effectiveDateDisabled, startDay);
    if (!isTodayTheFirst()) {
      if (isTimeZoneEnabled) {
        startDay.setDate(startDay.getDate() + 1);
        startDay = getStartDay(effectiveDateDisabled, startDay);
      } else {
        startDay = dayjs(startDay).utc(true).add(1, 'day').toDate();
      }
    }

    const copyStart = new Date(startDay);

    if (isTimeZoneEnabled) {
      dayOffset = getAdjustedDayOffset(effectiveDateDisabled, dayOffset, copyStart);
    } else {
      const result = checkDatesAndDays(dateDisabled, dayOffset, copyStart);
      if (result) {
        dayOffset = dayOffset + result;
      }
    }

    function countOccurrencesOfDays(dayOfWeekAbbr: string, nextKDays: Date[]) {
      const dayOfWeekMap = {
        sun: 0,
        mon: 1,
        tue: 2,
        wed: 3,
        thu: 4,
        fri: 5,
        sat: 6,
      };

      const dayOfWeekToCheck = dayOfWeekMap[dayOfWeekAbbr as keyof typeof dayOfWeekMap];
      let count = 0;

      nextKDays.forEach((day: Date) => {
        if (day.getDay() === dayOfWeekToCheck) {
          count++;
        }
      });

      return count;
    }

    function isDateInNextKDays(dateString: string, nextKDays: { toDateString: () => string }[]) {
      const [day, month, year] = dateString.split('-').map(Number);
      const dateToCheck = new Date(year, month - 1, day);
      return nextKDays.some(
        (day: { toDateString: () => string }) => day.toDateString() === dateToCheck.toDateString()
      );
    }

    function getStartDay(dateDisabled: string, startDate: Date): Date {
      const elements = dateDisabled.split(',').map((element: string) => element.trim());

      elements.forEach((element: string) => {
        if (element.includes('-')) {
          if (isDateInNextKDays(element, [startDate])) {
            startDate.setDate(startDay.getDate() + 1);
          }
        } else if (getDayKey(startDate) === element) {
          startDate.setDate(startDate.getDate() + 1);
        }
      });
      return startDate;
    }

    function checkDatesAndDays(dateDisabled: string, K: number, startDate: Date) {
      const nextKDays = getNextKDays(K, startDate);
      const elements = dateDisabled.split(',').map((element: string) => element.trim());
      let countMatches = 0;

      elements.forEach((element: string) => {
        if (element.includes('-')) {
          if (isDateInNextKDays(element, nextKDays)) {
            countMatches++;
          }
        } else {
          const dayCount = countOccurrencesOfDays(element.toLowerCase(), nextKDays);
          countMatches += dayCount;
        }
      });
      return countMatches;
    }

    function getAdjustedDayOffset(dateDisabled: string, baseOffset: number, startDate: Date) {
      let adjustedOffset = baseOffset;
      let previousOffset = -1;

      while (adjustedOffset !== previousOffset) {
        previousOffset = adjustedOffset;
        const disabledCount = checkDatesAndDays(dateDisabled, adjustedOffset, new Date(startDate));
        adjustedOffset = baseOffset + disabledCount;
      }

      return adjustedOffset;
    }
    let MinimumDate = '';
    let MaximumDate = '';
    if (showNextPreviousDate && showNextPreviousDate !== DatePickerValidationEnum.ALL_DATES) {
      if (showNextPreviousDate === DatePickerValidationEnum.ONLY_TODAY) {
        minDate = today;
        maxDate = today;

        this.input.min = isTimeZoneEnabled
          ? formatDateForInput(minDate)
          : minDate.toISOString().split('T')[0];
        this.input.max = isTimeZoneEnabled
          ? formatDateForInput(maxDate)
          : maxDate.toISOString().split('T')[0];

        MinimumDate = this.input.min;
        MaximumDate = this.input.max;
      }
      if (showNextPreviousDate === DatePickerValidationEnum.ONLY_FUTURE_DATES) {
        if (dayOffset === 0) {
          minDate = startDay;
        } else {
          minDate = startDay;
          maxDate = dayjs(minDate).utc(true).add(dayOffset, 'day').toDate();
        }
      } else if (showNextPreviousDate === DatePickerValidationEnum.ONLY_PAST_DATES) {
        let checkDatePastRange = true;

        const yearMin = parseInt(yearToRemoveDateMin ?? '0', 10);
        const yearMax = parseInt(yearToRemoveDateMax ?? '0', 10);

        if (yearMax !== 0 && yearMin !== 0) {
          minDate = dayjs(startDay).utc(true).subtract(yearMin, 'year').toDate();
          maxDate = dayjs(startDay).utc(true).subtract(yearMax, 'year').toDate();
          checkDatePastRange = false;
        } else if (yearMax !== 0 && yearMin === 0) {
          minDate = new Date(1900, 0, 1);
          maxDate = dayjs(startDay).utc(true).subtract(yearMax, 'year').toDate();
          checkDatePastRange = false;
        } else if (yearMax === 0 && yearMin !== 0) {
          minDate = dayjs(startDay).utc(true).subtract(yearMin, 'year').toDate();
          maxDate = startDay;
          checkDatePastRange = false;
        } else if (yearMax === 0 && yearMin === 0) {
          minDate = new Date(1900, 0, 1);
          maxDate = startDay;
          checkDatePastRange = false;
        }

        if (checkDatePastRange) {
          maxDate = today;
        }
      }

      this.input.min = isTimeZoneEnabled
        ? formatDateForInput(minDate)
        : minDate.toISOString().split('T')[0];
      this.input.max = isTimeZoneEnabled
        ? formatDateForInput(maxDate)
        : maxDate.toISOString().split('T')[0];

      if (showNextPreviousDate === DatePickerValidationEnum.ALL_DATES) {
        MinimumDate = '';
        MaximumDate = '';
      } else if (
        showNextPreviousDate === DatePickerValidationEnum.ONLY_FUTURE_DATES &&
        dayOffset === 0
      ) {
        MinimumDate = this.input.min;
        MaximumDate = isTimeZoneEnabled
          ? formatDateForInput(dayjs(today).add(2, 'year').toDate())
          : dayjs(today).add(2, 'year').toISOString().split('T')[0];
      } else {
        MinimumDate = this.input.min;
        MaximumDate = this.input.max;
      }
    }

    const nextAvailableDate = isTimeZoneEnabled ? createDateOnly(startDay) : startDay;

    function getNextKDays(K: number, startDay: Date) {
      const days = [];
      for (let i = 0; i <= K; i++) {
        days.push(new Date(startDay));
        startDay.setDate(startDay.getDate() + 1);
      }
      return days;
    }

    // Do not set a default date if placeholder is set,
    // otherwise set it to MaximumDate
    let defaultDate: Date | string = placeHolder ? '' : MaximumDate;

    if (!placeHolder) {
      const minDate = dayjs(MinimumDate).utc(true).toDate();
      const maxDate = dayjs(MaximumDate).utc(true).toDate();

      if (nextAvailableDate >= minDate && nextAvailableDate <= maxDate) {
        // Set defaultDate to nextAvailableDate if it is within the range
        defaultDate = nextAvailableDate;
      } else {
        // Set defaultDate to minDate if nextAvailableDate is before minDate,
        // otherwise set it to maxDate if it is after maxDate
        defaultDate = nextAvailableDate < minDate ? minDate : maxDate;
      }
    }

    if (
      defaultDate &&
      showNextPreviousDate &&
      showNextPreviousDate !== DatePickerValidationEnum.ALL_DATES
    ) {
      MaximumDate = isTimeZoneEnabled
        ? formatDateForInput(dayjs(defaultDate).add(dayOffset, 'day').toDate())
        : dayjs(defaultDate).add(dayOffset, 'day').toISOString().split('T')[0];
    }
    const options: FlatpickrOptions = {
      plugins: [
        yearDropdownPlugin({
          selectedYear:
            showNextPreviousDate === 'next'
              ? dayjs().year().toString()
              : dayjs(MaximumDate).utc(true).year().toString(),
          yearStart: dayjs(MinimumDate).utc(true).year(),
          yearEnd: dayjs(MaximumDate).utc(true).year(),
          defaultYear: defaultDateValue ? dayjs(defaultDateValue).utc(true).year().toString() : '',
        }) as Plugin<any>,
      ],
      dateFormat: 'Y-m-d',
      altFormat: this.dateFormat,
      altInput: true,
      allowInput: true,
      clickOpens: true,
      disableMobile: true,
      closeOnSelect: true,
      shorthandCurrentMonth: true,
      monthSelectorType: 'dropdown',
      locale: langLocaleMap[getPageLocale()] as any,
      defaultDate,
      minDate: MinimumDate,
      maxDate: MaximumDate,
      disable: [
        function (date: Date) {
          return disabledDays.includes(date.getDay());
        },
        ...disabledDates,
      ],
      onChange: (_selectedDates, dateStr) => {
        const flatpickrError = this.input.parentElement?.querySelector('.flatpickr-error');
        const inputText = this.input?.closest('.text');

        if (
          (!dateStr || isNaN(Date.parse(dateStr))) &&
          (this.input.getAttribute('data-validation')?.includes('vr-mandatory-field') ||
            this.input.hasAttribute('required'))
        ) {
          addFlatpickerError(flatpickrError, inputText);
        } else {
          resetInputError(this.input);
          removeFlatpickerError(flatpickrError, inputText);
        }
      },
      onClose: (selectedDates, dateStr) => {
        if (!selectedDates || selectedDates.length === 0)
          setCalendarToMonthYear(this.instance, defaultDateValue);

        if (
          (!dateStr || isNaN(Date.parse(dateStr))) &&
          (this.input.getAttribute('data-validation')?.includes('vr-mandatory-field') ||
            this.input.hasAttribute('required'))
        ) {
          const flatpickrError = this.input.parentElement?.querySelector('.flatpickr-error');
          const inputText = this.input?.closest('.text');
          addFlatpickerError(flatpickrError, inputText);
        } else {
          resetInputError(this.input);
        }
      },
      onReady: (_selectedDates, _dateStr, instance) => {
        instance.calendarContainer.classList.add(`${this.input.id}`);
        const monthDropdown = instance.monthsDropdownContainer;
        const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
        let isOpened = false;

        if (monthDropdown) {
          const updateMonthNames = (useLonghand: boolean) => {
            isOpened = useLonghand;
            monthDropdown.childNodes.forEach((option, index) => {
              if (option instanceof HTMLElement) {
                const monthIndex = parseInt(option.getAttribute('value') as string) ?? index;
                option.textContent = useLonghand
                  ? instance.l10n.months.longhand[monthIndex]
                  : instance.l10n.months.shorthand[monthIndex];
              }
            });
          };

          if (isMac) {
            // macOS-specific behavior
            monthDropdown.addEventListener('mouseout', () => updateMonthNames(false));
            monthDropdown.addEventListener('mousedown', () => updateMonthNames(true));
            monthDropdown.addEventListener('blur', () => updateMonthNames(false));
          } else {
            // General behavior
            monthDropdown.addEventListener('click', () => {
              if (isOpened) updateMonthNames(false);
              else updateMonthNames(true);
            });

            document.addEventListener('click', (event) => {
              const target = event.target as HTMLElement;
              if (!monthDropdown.contains(target) && isOpened) updateMonthNames(false);
            });
          }

          if (this.input.hasAttribute('data-date-prefill')) {
            selectFirstAvailableDay(instance);
          }
        }
      },
    };

    this.instance = flatpickr(this.input, options);
    setCalendarToMonthYear(this.instance, defaultDateValue);
    this.onScrollFunction(this.instance, this.input);

    const hourSelect = this.input.parentElement?.parentElement?.parentElement?.querySelector(
      'select[name="callback.selectedHour"]'
    ) as HTMLSelectElement | null;
    if (hourSelect) {
      getParameterFromGenericListTimeSlot(false, hourSelect.id, hourSelect);
    }
  }

  private onScrollFunction(instance: FlatpickrInstance, input: HTMLInputElement): void {
    window.addEventListener(
      'scroll',
      () => {
        function isSafariOniPad() {
          return /iPad|Macintosh/.test(navigator.userAgent) && 'ontouchend' in document;
        }
        const selects = document.querySelectorAll('select');
        if (isSafariOniPad()) {
          selects.forEach((select) => {
            requestAnimationFrame(() => {
              const parent = select.parentNode;
              parent?.removeChild(select);
              parent?.appendChild(select);
            });
          });
        }
        const calendar = document.querySelector(`.${this.input.id}`) as HTMLElement;
        if (
          calendar?.classList.contains('open') &&
          (window.innerWidth <= breakpoints.xl ||
            /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent))
        ) {
          const inputRect = input.parentElement
            ?.querySelectorAll('input')[1]
            .getBoundingClientRect();
          if (inputRect) {
            if (inputRect.bottom + window.scrollY > 0) {
              calendar.style.top = `${inputRect?.bottom + window.scrollY}px`;
            }
          }
        } else if (instance.isOpen) {
          instance.close();
        }
      },
      true
    );
  }
}

type ParsedDates = {
  disabledDays: number[];
  disabledDates: Date[];
};

function appendDisabledDays(dateDisabled: string, includeWeekend: boolean): string {
  if (!includeWeekend) {
    return dateDisabled;
  }

  const items = dateDisabled
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
  const existingItems = new Set(items.map((item) => item.toLowerCase()));

  ['sat', 'sun'].forEach((day) => {
    if (!existingItems.has(day)) {
      items.push(day);
    }
  });

  return items.join(',');
}

function createDateOnly(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function formatDateForInput(date: Date): string {
  return dayjs(date).format('YYYY-MM-DD');
}

function processExcludedDates(dateDisabled: string): ParsedDates {
  const disabledDays: number[] = [];
  const disabledDates: Date[] = [];

  const daysMap: { [key: string]: number } = {
    sun: 0,
    mon: 1,
    tue: 2,
    wed: 3,
    thu: 4,
    fri: 5,
    sat: 6,
  };

  const items = dateDisabled.split(',');

  items.forEach((item) => {
    if (item.length === 3) {
      const dayNumber = daysMap[item.toLowerCase()];
      if (dayNumber !== undefined) {
        disabledDays.push(dayNumber);
      }
    } else {
      const dateParts = item.split('-');
      if (dateParts.length === 3) {
        const day = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1;
        const year = parseInt(dateParts[2], 10);

        const date = new Date(year, month, day);
        disabledDates.push(date);
      }
    }
  });

  return { disabledDays, disabledDates };
}
export function initializeDatePickers(): void {
  document.querySelectorAll('.cmp-form-text.date').forEach((node) => {
    const datePicker = new DatePicker(node as HTMLElement);
    datePicker.init();
  });

  // track the current input date
  let currentInputDate = '';
  let flatpickrErrorElement: Element | null | undefined;

  const inputFields: NodeListOf<HTMLInputElement> = document.querySelectorAll(
    'input.cmp-form-text__text.input'
  );
  inputFields.forEach((inputField: HTMLInputElement) => {
    if (!inputField.hasAttribute('inputMode')) {
      inputField.setAttribute('inputMode', 'numeric');
    }
    inputField.addEventListener('input', formatInput); // Add event listener for input (user types)
    inputField.addEventListener('keydown', formatInput); // Add event listener for keydown (user presses key e.g backspace)
    inputField.addEventListener('blur', () => {
      if (!inputField?.classList?.contains('active')) {
        checkDateFormat(inputField, flatpickrErrorElement, currentInputDate);
        currentInputDate = '';
        resetInputError(inputField);
      }
    });

    inputField.addEventListener('focus', () => {
      flatpickrErrorElement = inputField.parentElement?.querySelector('.flatpickr-error');
      removeFlatpickerError(flatpickrErrorElement, inputField?.closest('.text'));
    });

    inputField.addEventListener('keyup', (e) => {
      currentInputDate = (e.target as HTMLInputElement).value;
    });

    // Handle backspace behavior
    inputField.addEventListener('keydown', function (e: KeyboardEvent) {
      // check date format on tab
      if (e.key === 'Tab') {
        if (inputField?.classList?.contains('active')) {
          checkDateFormat(inputField, flatpickrErrorElement, currentInputDate);
          currentInputDate = '';
          resetInputError(inputField);
        }
      }
    });
  });
}

function refreshCallbackDatePickersOnTimeZoneEnable(): void {
  document
    .querySelectorAll<HTMLInputElement>('input[name="callback.selectedDay"]')
    .forEach((input) => {
      const instanceHolder = input as HTMLInputElement & { _flatpickr?: FlatpickrInstance };
      const flatpickrInstance = instanceHolder._flatpickr;
      const container = input.closest('.cmp-form-text.date') as HTMLElement | null;

      if (flatpickrInstance && container) {
        flatpickrInstance.destroy();
        const datePicker = new DatePicker(container);
        datePicker.init();
      }
    });
}

if (!getTimeZoneEnable()) {
  const unsubscribe = store.subscribe(() => {
    if (getTimeZoneEnable()) {
      unsubscribe();
      refreshCallbackDatePickersOnTimeZoneEnable();
    }
  });
}

function formatInput(event: any): void {
  const target = event.target as HTMLInputElement;
  const inputValue = target.value.replace(/\D/g, ''); // Remove all non-digit characters
  let placeholder = target.getAttribute('placeholder') ?? '';
  let noPlaceholder = false;
  // Define a regex pattern for valid date formats like dd/mm/yyyy, mm/dd/yyyy, etc.
  const dateFormatPattern = /^(d{1,2}|D{1,2})\/(m{1,2}|M{1,2})\/(y{2,4}|Y{2,4})$/;

  if (!dateFormatPattern.test(placeholder)) {
    // If placeholder is invalid or not a date format, get it from data-date-format
    const previousInput = target.previousElementSibling as HTMLInputElement;
    const dateFormat = previousInput?.getAttribute('data-date-format') ?? '';
    placeholder = dateFormat.replace(/d/g, 'dd').replace(/m/g, 'mm').replace(/[Yy]/g, 'yyyy');
    noPlaceholder = true;
  }
  const prevValue = inputValue.substring(0, inputValue.length - 1); //save previous value without last character typed incase of invalid date
  const prevPlaceholder = placeholder.substring(
    prevValue.length === 0 ? 0 : prevValue.length,
    placeholder.length
  ); //save previous placeholder without last character typed incase of invalid date
  // If no valid numbers are entered, clear the input
  if (inputValue.length === 0) {
    target.value = '';
    return;
  }

  let formattedValue = '';
  let cursor = 0;

  // Capture the original cursor position and selection length
  let selectionStart = target.selectionStart ?? 0;
  const selectionEnd = target.selectionEnd ?? 0;
  const isBackspace = event.key === 'Backspace';
  let isInvalid = false;
  // If the entire field is selected, allow deletion without formatting
  if (isBackspace && selectionStart === 0 && selectionEnd === target.value.length) {
    target.value = '';
    return;
  }
  if (noPlaceholder) {
    target.placeholder = placeholder;
  }

  const isNumberKey = /\d/.test(event.key);

  // Clear input and start fresh if all text is selected and a number is typed
  if (selectionStart === 0 && selectionEnd === target.value.length && isNumberKey) {
    target.value = event.key + placeholder.substring(-1, placeholder.length);
    const keydownEvent = new KeyboardEvent('keydown', {
      key: '',
      bubbles: true,
      cancelable: true,
    });
    target.dispatchEvent(keydownEvent); // Trigger keydown event to validate the date after selected all text and a number is typed
    event.preventDefault();
    return;
  }

  // Save the indexes of the day, month, and year to map different date formats
  // e.g. dd/mm/yyyy -> d, m, y

  const dayIndex = placeholder.toLowerCase().indexOf('d');
  const monthIndex = placeholder.toLowerCase().indexOf('m');

  // Function to validate the day based on the month and year
  function isValidDate(day: number, month: number, year: number): boolean {
    // Check if month is February and day is 29, then validate for leap year
    if (month === 2 && day === 29) {
      return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0; // Leap year check
    }

    // For other months, get the correct days in the specified month and year
    const daysInMonth = new Date(year, month, 0).getDate();
    return day > 0 && day <= daysInMonth;
  }

  // Iterate through each character in the placeholder
  for (const char of placeholder) {
    if (char === '/') {
      formattedValue += '/'; // Add slashes from the placeholder
      if (selectionStart === formattedValue.length - 1 && !isBackspace && !isInvalid) {
        selectionStart++; // Move cursor forward if entering, skip if backspacing
      }
    } else if (/[dDmMyY]/.test(char)) {
      // Check if the character is a 'd', 'm', or 'y'
      if (cursor < inputValue.length) {
        const inputChar = inputValue[cursor];
        // Handle day validation
        if (char.toLowerCase() === 'd') {
          // Initialize day, month, and year variables
          let dayInput = inputValue.substring(2, 4);
          let monthInput = inputValue.substring(0, 2) || '1';
          const yearInput = inputValue.substring(4, 8) || '1996';
          // if the day index is before the month index, set the dayInput to the first two digits and monthInput to the last two
          if (dayIndex < monthIndex) {
            dayInput = inputValue.substring(0, 2);
            monthInput = inputValue.substring(2, 4) || '1';
          }
          // If the input is only 1 digit between 4 and 9, add a leading zero. eg 4 -> 04, 9 -> 09
          if (Number(inputChar) >= 4 && Number(inputChar) < 10 && dayInput.length === 1) {
            formattedValue += '0' + inputChar;
            cursor++;
            selectionStart++;
            dayInput = '0' + dayInput;
            // If the input is 0 allow leading zero
          } else if (
            (Number(dayInput) < 10 &&
              inputChar === '0' &&
              (inputValue.length === 1 || inputValue.length === 3)) ||
            (dayInput === '29' && monthInput === '02') ||
            isValidDate(Number(dayInput), Number(monthInput), Number(yearInput))
          ) {
            formattedValue += inputChar; // Insert typed day
            cursor++;
          } else {
            isInvalid = true;
            break;
          }
        }

        // Handle month validation
        else if (char.toLowerCase() === 'm') {
          // Initialize month variable
          let monthInput = inputValue.substring(2, 4);
          // if the month index is before the day index, set the monthInput to the first two digits
          if (monthIndex < dayIndex) {
            monthInput = inputValue.substring(0, 2);
          }
          // If the input is only 1 digit between 1 and 9, add a leading zero. eg 2 -> 02, 9 -> 09
          if (Number(inputChar) > 1 && Number(inputChar) < 10 && monthInput.length === 1) {
            formattedValue += '0' + inputChar;
            cursor++;
            selectionStart++;
            monthInput = '0' + monthInput;
            // If the input is 0 allow leading zero
          } else if (
            (Number(monthInput) < 10 &&
              inputChar === '0' &&
              (inputValue.length === 3 || inputValue.length === 1)) ||
            (Number(monthInput) >= 1 && Number(monthInput) <= 12)
          ) {
            formattedValue += inputChar; // Insert typed month
            cursor++;
          } else {
            isInvalid = true;
            break;
          }
        }

        // Handle year validation
        else if (char.toLowerCase() === 'y') {
          // Initialize day, month, and year variables
          let dayInput = inputValue.substring(2, 4);
          let monthInput = inputValue.substring(0, 2) || '1';
          const yearInput = inputValue.substring(4, 8) || '1996';
          // if the day index is before the month index, set the dayInput to the first two digits and monthInput to the last two
          if (dayIndex < monthIndex) {
            dayInput = inputValue.substring(0, 2);
            monthInput = inputValue.substring(2, 4) || '1';
          }
          // If the date is valid, insert the typed character
          if (
            (yearInput.length === 4 &&
              isValidDate(Number(dayInput), Number(monthInput), Number(yearInput))) ||
            yearInput.length < 4
          ) {
            formattedValue += inputChar; // Insert typed day
            cursor++;
          } else {
            isInvalid = true;
            break;
          }
        }
      } else {
        formattedValue += char; // Add placeholder character if input is not fully typed
      }
    } else {
      formattedValue += char; // Add other characters as-is
    }
  }
  // If the input is invalid, restore the previous value + placeholder
  if (isInvalid) {
    target.value = prevValue + prevPlaceholder;
    selectionStart =
      prevPlaceholder.toLowerCase().startsWith('mm') || prevPlaceholder.startsWith('dd')
        ? inputValue.length
        : prevPlaceholder.toLowerCase().startsWith('yyy')
        ? inputValue.length + 1
        : inputValue.length - 1;
    const keydownEvent = new KeyboardEvent('keydown', {
      key: '',
      bubbles: true,
      cancelable: true,
    });
    target.dispatchEvent(keydownEvent); // Trigger keydown event to restore cursor position
  } else if (formattedValue) {
    const splittedFormatedValue = formattedValue.split('/');
    const firstIndex = splittedFormatedValue[0];
    const secondIndex = splittedFormatedValue[1];
    const thirdIndex = splittedFormatedValue[2];
    // Handle value and slash if it's 3 digits in each index to resolve the problem with '03d/mm/yyyy' or '03/03m/yyyy'
    if (firstIndex.length > 2) {
      formattedValue = firstIndex.substring(0, 2) + '/' + secondIndex + '/' + thirdIndex;
      selectionStart++;
    }
    if (secondIndex.length > 2) {
      formattedValue = firstIndex + '/' + secondIndex.substring(0, 2) + '/' + thirdIndex;
      selectionStart++;
    }
    target.value = formattedValue; // Set the formatted value anyway
  }

  // Handle backspace over slashes
  if (isBackspace && selectionStart > 0 && formattedValue[selectionStart - 1] === '/') {
    selectionStart--; // Move cursor back over the slash
  }

  // Set the updated cursor position
  target.setSelectionRange(selectionStart, selectionStart);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeDatePickers);
} else {
  initializeDatePickers();
}
function resetInputError(input: any) {
  const errorList = input.parentElement.querySelectorAll('.error-list-validation div');
  errorList.forEach(function (element: HTMLElement) {
    element.classList.add('input-hidden-error-messagge-vr');
    element?.closest('.text')?.classList?.remove('error-input');
  });
  if (
    input.parentElement.parentElement.parentElement.querySelector(
      'select[name="callback.selectedHour"]'
    )
  ) {
    const hourSelect = input.parentElement.parentElement.parentElement.querySelector(
      'select[name="callback.selectedHour"]'
    );
    getParameterFromGenericListTimeSlot(false, hourSelect.id, hourSelect);
  }
}

/*
  This function checks for invalid dates and applies the appropriate error classes
  We need to use the customParseFormat plugin to have formats like 'DD/MM/YYYY'
  Cases:
    1. If we have 'all' available dates we dont need to check something.
       Datepicker will auto complete all dates if we enter something like '25/10'
    2. For the cases of 'past' and 'next' days we need to check the valid ranges.
       For this we use the max and min attributes of the input element.
       Also we use the currenInputDate we tracked with keydown listener.
       This is because if an invalid date is entered the datepicker clears the
       date and the value of the input becomes empty before we catch it.
    3. Before we do anything we check if we have as input somethink like '25/10'
       or '10/25'. This is an edge case where we must let the datepicker auto complete the date
       and not show error.
  Notice:
      If we have this format 'm/d/Y' and we enter '25/10/2024' is invalid format
      If we have the above format and the max date is '26/10/2024' is invalid date
      Since we have both the above scenarios the generic message can be something like
      Try again: invalid date or invalid format
*/

function checkDateFormat(input: any, flatpickrErrorElement: any, currentInputDate: any) {
  const actualInput = input.previousElementSibling;
  const AEMFormat = actualInput.getAttribute('data-date-format');

  const maxDate = dayjs(actualInput.getAttribute('max')).utc(true).toDate();
  const minDate = dayjs(actualInput.getAttribute('min')).utc(true).toDate();
  const dataDates = actualInput.getAttribute('data-date-values');

  let currentDate: Date | null = null;

  const format = getFormatFromAEMConfig(AEMFormat);

  if (dataDates === 'all') {
    return;
  }

  const checkAC = checkNextDaysAutoComplete(currentInputDate, dataDates, AEMFormat, format);

  if (checkAC) {
    currentDate = checkAC;
  } else {
    currentDate = dayjs(currentInputDate, format).utc(true).toDate();
  }
  const inputDateValue = dayjs(actualInput.value).utc(true).toDate();

  const dateParentDiv = input?.closest('.text');
  if (currentInputDate !== '') {
    if (
      currentDate < minDate ||
      currentDate > maxDate ||
      (isNaN(currentDate.getTime()) && isNaN(inputDateValue.getTime()))
    ) {
      addFlatpickerError(flatpickrErrorElement, dateParentDiv);
      resetInputError(input);
    } else {
      removeFlatpickerError(flatpickrErrorElement, dateParentDiv);
    }
  }
}

function addFlatpickerError(
  flatpickrErrorElement: Element | null | undefined,
  dateParentDiv: Element | null
) {
  flatpickrErrorElement?.classList?.remove('hidden');
  dateParentDiv?.classList?.add('flatepickr-error-input');
}

function removeFlatpickerError(
  flatpickrErrorElement: Element | null | undefined,
  dateParentDiv: Element | null
) {
  flatpickrErrorElement?.classList?.add('hidden');
  dateParentDiv?.classList?.remove('flatepickr-error-input');
}

export function setCalendarToMonthYear(fp: FlatpickrInstance, defaultDateValue: string | null) {
  // If configuration is not set, do not change anything
  if (!defaultDateValue) return;

  // Change calendar month to January (0)
  fp.changeMonth(0, false);
  // Then, move forward to the correct month (0-11)
  fp.changeMonth(dayjs(defaultDateValue).utc(true).month());
  // Change calendar year
  fp.changeYear(dayjs(defaultDateValue).utc(true).year());
}
